import { ErrorCode, type ApiErrorBody } from '@qualicloud/contracts';

/**
 * Erro de API tipado.
 *
 * O `code` é o contrato estável (architecture.md §9): os testes de OQ verificam
 * o CÓDIGO, nunca a mensagem, exatamente para que a tradução possa mudar sem
 * invalidar evidência de validação. A mensagem exibida ao usuário sai daqui,
 * do dicionário `ERROR_MESSAGES_PT_BR`, e não do texto que o servidor mandou —
 * assim a interface é consistente mesmo quando um serviço devolve mensagem
 * genérica.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fieldErrors: Readonly<Record<string, string[]>>;
  readonly correlationId: string | null;
  /** Mensagem original do servidor, preservada para a trilha e o suporte. */
  readonly serverMessage: string;

  constructor(params: {
    code: string;
    status: number;
    serverMessage: string;
    fieldErrors?: Record<string, string[]>;
    correlationId?: string | null;
  }) {
    super(translateErrorCode(params.code, params.serverMessage));
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.serverMessage = params.serverMessage;
    this.fieldErrors = Object.freeze(params.fieldErrors ?? {});
    this.correlationId = params.correlationId ?? null;
  }

  static fromBody(status: number, body: Partial<ApiErrorBody> | null): ApiError {
    return new ApiError({
      code: body?.code ?? ErrorCode.INTERNAL,
      status,
      serverMessage: body?.message ?? 'Erro não identificado.',
      fieldErrors: body?.fieldErrors,
      correlationId: body?.correlationId ?? null,
    });
  }

  /** Erro de rede/timeout — não veio resposta HTTP nenhuma. */
  static network(cause?: unknown): ApiError {
    const error = new ApiError({
      code: 'NETWORK.UNAVAILABLE',
      status: 0,
      serverMessage: 'Falha de rede.',
    });
    if (cause instanceof Error) error.cause = cause;
    return error;
  }

  is(...codes: readonly string[]): boolean {
    return codes.includes(this.code);
  }
}

/**
 * `ErrorCode` → mensagem em português do Brasil.
 *
 * Regras de redação, aplicadas a todas as entradas:
 *  1. dizer O QUE aconteceu, em voz ativa;
 *  2. dizer O QUE FAZER a seguir, quando houver ação possível;
 *  3. nunca expor detalhe técnico nem nome de tabela;
 *  4. nunca revelar existência de recurso de outra organização — por isso
 *     CROSS_TENANT_ACCESS e NOT_FOUND têm exatamente o mesmo texto.
 */
export const ERROR_MESSAGES_PT_BR: Readonly<Record<string, string>> = Object.freeze({
  // ── Autenticação ──────────────────────────────────────────────────────────
  [ErrorCode.INVALID_CREDENTIALS]: 'Usuário ou senha incorretos.',
  [ErrorCode.ACCOUNT_LOCKED]:
    'Conta temporariamente bloqueada por excesso de tentativas. Aguarde o fim do bloqueio ou peça desbloqueio ao administrador.',
  [ErrorCode.ACCOUNT_INACTIVE]:
    'Esta conta está inativa. Procure o administrador da sua organização.',
  [ErrorCode.PASSWORD_EXPIRED]: 'Sua senha expirou. Defina uma nova senha para continuar.',
  [ErrorCode.MFA_REQUIRED]: 'Informe o código do seu aplicativo autenticador.',
  [ErrorCode.MFA_INVALID]: 'Código de verificação inválido ou expirado. Gere um novo código.',
  [ErrorCode.SESSION_EXPIRED]: 'Sua sessão expirou. Entre novamente para continuar.',
  [ErrorCode.FORBIDDEN]: 'Você não tem permissão para executar esta ação.',

  // ── Política de senha — §11.300(b) ────────────────────────────────────────
  [ErrorCode.PASSWORD_TOO_SHORT]: 'A senha é mais curta que o mínimo exigido pela organização.',
  [ErrorCode.PASSWORD_MISSING_UPPERCASE]: 'A senha precisa conter ao menos uma letra maiúscula.',
  [ErrorCode.PASSWORD_MISSING_LOWERCASE]: 'A senha precisa conter ao menos uma letra minúscula.',
  [ErrorCode.PASSWORD_MISSING_DIGIT]: 'A senha precisa conter ao menos um número.',
  [ErrorCode.PASSWORD_MISSING_SYMBOL]:
    'A senha precisa conter ao menos um símbolo (por exemplo: ! @ # $ %).',
  [ErrorCode.PASSWORD_REUSED]:
    'Esta senha já foi usada recentemente. Escolha uma senha que você ainda não utilizou.',
  [ErrorCode.PASSWORD_TOO_COMMON]:
    'Esta senha aparece em listas públicas de senhas vazadas. Escolha outra.',

  // ── Assinatura eletrônica — 21 CFR Part 11 Subparte C ─────────────────────
  [ErrorCode.SIGNATURE_PROOF_INVALID]:
    'A confirmação de identidade não pôde ser validada. Refaça a assinatura. Nenhuma alteração foi registrada.',
  [ErrorCode.SIGNATURE_PROOF_EXPIRED]:
    'O prazo de 60 segundos para concluir a assinatura terminou. Confirme sua identidade novamente. Nenhuma alteração foi registrada.',
  [ErrorCode.SIGNATURE_PROOF_ALREADY_USED]:
    'Esta confirmação de identidade já foi utilizada. Cada assinatura vale para um único ato. Refaça a confirmação se quiser assinar de novo.',
  [ErrorCode.SIGNATURE_PROOF_RECORD_MISMATCH]:
    'A confirmação de identidade não corresponde ao registro que está sendo assinado. Feche esta janela e refaça a operação a partir do documento correto.',
  [ErrorCode.SIGNATURE_IDENTITY_MISMATCH]:
    'A assinatura só pode ser executada pelo titular da sessão. Informe o seu próprio usuário — não existe delegação nem procuração de assinatura.',
  [ErrorCode.SIGNATURE_REASON_REQUIRED]: 'Este ato exige justificativa. Descreva o motivo.',
  [ErrorCode.SIGNATURE_NOT_AUTHORIZED_FOR_ACT]:
    'Você não tem autoridade para executar este ato assinado. Procure o gestor da qualidade da sua organização.',
  [ErrorCode.SIGNATURE_RECORD_MUTATED]:
    'O registro foi alterado por outra pessoa depois que você abriu esta tela. Recarregue e confira o conteúdo antes de assinar.',

  // ── Multi-tenancy ─────────────────────────────────────────────────────────
  // Texto idêntico ao NOT_FOUND por decisão de segurança (errors.ts do contrato):
  // um texto diferente confirmaria a existência do recurso em outra organização.
  [ErrorCode.CROSS_TENANT_ACCESS]: 'Registro não encontrado.',
  [ErrorCode.ORGANIZATION_CONTEXT_MISSING]:
    'Não foi possível identificar a organização da sessão. Entre novamente.',

  // ── Documento ─────────────────────────────────────────────────────────────
  [ErrorCode.DOCUMENT_NAME_DUPLICATE]:
    'Já existe um documento com este nome na organização. Escolha outro nome.',
  [ErrorCode.DOCUMENT_NEW_FILE_REQUIRED]:
    'Alterar os dados do documento exige anexar um novo arquivo. Se quiser mudar apenas a distribuição, use a aba Distribuição — ela não gera nova versão.',
  [ErrorCode.DOCUMENT_VERSION_REGRESSION]:
    'A versão informada é anterior à versão vigente. Informe um número maior ou deixe o campo em branco para o sistema calcular.',
  [ErrorCode.DOCUMENT_VERSION_FORMAT]:
    'Formato de versão inválido. Use <maior>.<menor>, por exemplo 2.0.',
  [ErrorCode.DOCUMENT_ILLEGAL_TRANSITION]:
    'Esta operação não é permitida na situação atual do documento. Recarregue a página para ver a situação vigente.',
  [ErrorCode.DOCUMENT_NOT_APPROVER]:
    'Você não está entre os aprovadores designados desta versão.',
  [ErrorCode.DOCUMENT_ALREADY_DECIDED]:
    'Esta versão já recebeu a sua decisão. Cada aprovador decide uma única vez por versão.',
  [ErrorCode.DOCUMENT_SELF_APPROVAL]:
    'O autor da versão não pode aprová-la. A segregação de funções é exigida pela ISO 13485 §4.2.4 e está ativa nesta organização.',
  [ErrorCode.DOCUMENT_APPROVAL_CONFIG_INVALID]:
    'A configuração de aprovação do documento é inválida. Verifique a política e o quórum na aba Dados.',
  [ErrorCode.DOCUMENT_OBSOLETE_DOWNLOAD_BLOCKED]:
    'O download de documento obsoleto está bloqueado para impedir uso não intencional (ISO 13485 §4.2.4).',
  [ErrorCode.DOCUMENT_TYPE_CYCLE]:
    'Não é possível mover um tipo para dentro de um subtipo dele mesmo.',
  [ErrorCode.DOCUMENT_TYPE_IN_USE]:
    'Este tipo não pode ser excluído porque há documentos ou subtipos vinculados a ele.',

  // ── Catálogos do SGQ: tipo de reunião e tipo de solicitação ───────────────
  // As três mensagens abaixo apontam a MESMA saída — desativar — porque é ela
  // que resolve o caso real: tirar o tipo dos formulários sem apagar o
  // histórico que o cita. A tela deve chegar aqui raramente: `system` e
  // `usageCount` vêm na listagem justamente para avisar antes do clique.
  [ErrorCode.MEETING_TYPE_IN_USE]:
    'Há reuniões usando este tipo, por isso ele não pode ser excluído. Desative o tipo: ele deixa de aparecer nos formulários e as reuniões já registradas continuam íntegras.',
  [ErrorCode.REQUEST_TYPE_IN_USE]:
    'Há solicitações usando este tipo, por isso ele não pode ser excluído. Desative o tipo: ele deixa de aparecer nos formulários e as solicitações já registradas continuam íntegras.',
  [ErrorCode.SGQ_TYPE_SYSTEM_IMMUTABLE]:
    'Este tipo é nativo do sistema: o código não muda e o tipo não é excluído, porque registros assinados o citam. Você pode renomeá-lo, alterar as exigências ou desativá-lo.',
  [ErrorCode.SGQ_TYPE_INACTIVE]:
    'Este tipo está desativado e não pode ser usado em registros novos. Escolha um tipo ativo ou reative este na administração de tipos.',
  [ErrorCode.CAPA_STANDALONE_NOT_ALLOWED]:
    'O tipo de solicitação escolhido exige uma causa-raiz e não admite abertura avulsa. Abra a solicitação a partir da análise de causa ou escolha um tipo que dispense causa.',

  // ── Upload / mídia ────────────────────────────────────────────────────────
  [ErrorCode.FILE_TOO_LARGE]: 'O arquivo excede o tamanho máximo permitido de 20 MB.',
  [ErrorCode.FILE_EXTENSION_NOT_ALLOWED]: 'Extensão de arquivo não permitida.',
  [ErrorCode.FILE_MIME_MISMATCH]:
    'O conteúdo do arquivo não corresponde à extensão informada. Verifique se o arquivo não está corrompido ou renomeado.',
  [ErrorCode.FILE_INFECTED]:
    'O arquivo foi recusado pela verificação de antivírus e não foi armazenado.',
  [ErrorCode.FILE_CHECKSUM_MISMATCH]:
    'O arquivo chegou corrompido ao servidor. Tente enviar novamente.',
  [ErrorCode.FILE_RETENTION_LOCKED]:
    'Este arquivo está sob retenção legal e não pode ser removido nem substituído.',

  // ── NC / CAPA ─────────────────────────────────────────────────────────────
  [ErrorCode.NC_ILLEGAL_TRANSITION]:
    'Esta transição não é permitida na situação atual da não-conformidade.',
  [ErrorCode.NC_CAPA_INCOMPLETE]:
    'A não-conformidade não pode ser encerrada: há ações sem conclusão ou sem verificação de eficácia aprovada (ISO 13485 §8.5.2).',
  [ErrorCode.GUT_SCORE_INVALID]: 'Os valores de Gravidade, Urgência e Tendência devem ser de 1 a 5.',
  [ErrorCode.NC_RECURRENCE_SELF]:
    'Uma não-conformidade não pode ser reincidência de si mesma. Escolha o registro anterior que se repetiu.',

  [ErrorCode.CAPA_ILLEGAL_TRANSITION]:
    'Esta transição não é permitida na situação atual da ação corretiva.',
  [ErrorCode.CAPA_PLAN_INCOMPLETE]:
    'O plano de ação está incompleto. Preencha o que será feito, por quê, onde, quando, quem e como antes de prosseguir.',
  // Deixou de ser "toda ação precisa de causa": quem exige é o TIPO escolhido
  // (`requiresRootCause`). A mensagem precisa dizer as DUAS saídas, porque a
  // segunda — trocar o tipo — costuma ser a certa numa solicitação avulsa.
  [ErrorCode.CAPA_NO_ROOT_CAUSE]:
    'O tipo de solicitação escolhido exige a causa que ela ataca. Selecione a causa-raiz ou escolha um tipo que dispense análise de causa.',
  [ErrorCode.CAPA_NOT_THE_VERIFIER]:
    'A eficácia é verificada por quem solicitou a ação. Peça a verificação a essa pessoa.',
  [ErrorCode.CAPA_SELF_VERIFICATION]:
    'Quem executou a ação não verifica a própria eficácia. A verificação cabe a quem solicitou (ISO 13485 §8.5.2).',
  [ErrorCode.CAPA_NOT_THE_ASSIGNEE]:
    'Só o responsável pela ação registra a execução.',
  [ErrorCode.CAPA_REASSIGN_NOT_ALLOWED]:
    'Redesignar esta ação cabe ao gerente do departamento responsável.',

  // ── Reunião ───────────────────────────────────────────────────────────────
  [ErrorCode.MEETING_ILLEGAL_TRANSITION]:
    'Esta transição não é permitida na situação atual da reunião.',
  [ErrorCode.MEETING_NO_PARTICIPANTS]:
    'Convide ao menos um participante antes de abrir a reunião.',
  // O vínculo com não-conformidade deixou de ser regra de toda reunião: vale
  // só para os tipos com `requiresNonconformity`. O servidor manda uma versão
  // desta frase NOMEANDO o tipo; este texto é o fallback do dicionário.
  [ErrorCode.MEETING_NC_LINK_REQUIRED]:
    'O tipo de reunião escolhido exige ao menos uma não-conformidade vinculada. Vincule uma não-conformidade ou escolha outro tipo.',
  [ErrorCode.MEETING_NOT_PARTICIPANT]:
    'Só quem foi convidado registra presença nesta reunião.',
  [ErrorCode.MEETING_EFFECTIVENESS_NOT_APPLICABLE]:
    'Não há verificação de eficácia pendente para esta reunião.',

  // ── Circular ──────────────────────────────────────────────────────────────
  [ErrorCode.CIRCULAR_ILLEGAL_TRANSITION]:
    'Esta transição não é permitida na situação atual da circular.',
  [ErrorCode.CIRCULAR_ALREADY_ACKNOWLEDGED]:
    'Você já registrou ciência desta circular.',
  [ErrorCode.CIRCULAR_NOT_RECIPIENT]:
    'Esta circular não foi endereçada a você.',
  [ErrorCode.CIRCULAR_IMMUTABLE]:
    'Uma circular publicada não é alterada: o texto que os destinatários leram precisa continuar o mesmo. Publique uma nova no lugar.',

  // ── Auditoria ─────────────────────────────────────────────────────────────
  [ErrorCode.AUDIT_CHAIN_BROKEN]:
    'A verificação da cadeia de integridade da trilha de auditoria encontrou divergência. Comunique imediatamente o responsável pela qualidade.',
  [ErrorCode.AUDIT_IMMUTABLE]:
    'A trilha de auditoria é imutável: registros não podem ser alterados nem excluídos.',

  // ── Genéricos ─────────────────────────────────────────────────────────────
  [ErrorCode.VALIDATION_FAILED]: 'Há campos com preenchimento inválido. Revise o formulário.',
  [ErrorCode.NOT_FOUND]: 'Registro não encontrado.',
  [ErrorCode.CONFLICT]:
    'O registro foi alterado por outra pessoa. Recarregue a página e refaça a operação.',
  [ErrorCode.RATE_LIMITED]:
    'Muitas tentativas em pouco tempo. Aguarde alguns instantes antes de tentar novamente.',
  /*
   * Só entra em cena se o servidor não mandar texto. A frase fala com o USUÁRIO
   * do cliente, que não tem culpa nem poder sobre o contrato: ela diz a quem
   * recorrer em vez de acusá-lo de algo.
   */
  /*
   * Fala com o USUÁRIO do cliente, que não decide contrato nem configura
   * servidor de e-mail. Ela diz o que aconteceu e a quem recorrer, sem sugerir
   * que ele fez algo errado — e nomeia as DUAS saídas possíveis, porque a
   * empresa dele escolhe entre elas.
   */
  [ErrorCode.EMAIL_NO_TRANSPORT]:
    'Sua organização ainda não tem envio de e-mail: nem servidor próprio, nem o serviço contratado. As notificações continuam aparecendo aqui no sistema. Fale com quem administra o sistema na sua empresa.',
  [ErrorCode.MODULE_NOT_CONTRACTED]:
    'Este módulo não faz parte do plano contratado pela sua organização. Fale com quem administra o sistema na sua empresa.',
  [ErrorCode.INTERNAL]:
    'Ocorreu um erro inesperado. A operação não foi concluída. Se persistir, informe o código de correlação ao suporte.',

  // ── Cliente ───────────────────────────────────────────────────────────────
  'NETWORK.UNAVAILABLE':
    'Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.',
});

/**
 * Códigos em que a mensagem do SERVIDOR vence o dicionário.
 *
 * A regra geral é o contrário — dicionário primeiro, para que a interface fale
 * a mesma língua mesmo quando um serviço devolve texto genérico. A exceção vale
 * quando o servidor sabe algo que o dicionário não tem como saber: aqui, o NOME
 * do tipo que impôs a exigência. "O tipo SACAP exige uma não-conformidade
 * vinculada" resolve o problema do usuário; "o tipo escolhido exige" o deixa
 * conferindo qual era o tipo escolhido.
 *
 * A entrada do dicionário continua existindo e é usada quando o servidor não
 * mandou texto — nenhum código fica sem tradução.
 */
const SERVER_MESSAGE_PREFERRED_CODES: ReadonlySet<string> = new Set([
  ErrorCode.MEETING_NC_LINK_REQUIRED,
  /*
   * O gateway já monta a frase com o NOME do módulo — "Não-conformidade não faz
   * parte do plano contratado pela sua organização". Um texto fixo aqui diria
   * "recurso indisponível" e obrigaria quem atende o telefone a adivinhar qual
   * módulo era.
   */
  ErrorCode.MODULE_NOT_CONTRACTED,
]);

/**
 * Traduz um código de erro. Fallback em cascata:
 *   dicionário → mensagem do servidor → texto genérico.
 *
 * A mensagem do servidor entra como segundo nível porque o backend já escreve
 * em português (architecture.md §12) — um código novo ainda não mapeado aqui
 * produz texto útil em vez de "erro desconhecido".
 */
export function translateErrorCode(code: string, serverMessage?: string): string {
  const fromServer = serverMessage?.trim() ?? '';
  if (SERVER_MESSAGE_PREFERRED_CODES.has(code) && fromServer.length > 0) return fromServer;

  const mapped = ERROR_MESSAGES_PT_BR[code];
  if (mapped) return mapped;
  if (serverMessage && serverMessage.trim().length > 0) return serverMessage;
  return ERROR_MESSAGES_PT_BR[ErrorCode.INTERNAL] as string;
}

/** Converte qualquer coisa lançada numa mensagem exibível. */
export function toUserMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return ERROR_MESSAGES_PT_BR[ErrorCode.INTERNAL] as string;
}

/** Erros que NÃO devem ser reexecutados automaticamente pelo TanStack Query. */
export function isNonRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 0) return false; // rede: vale tentar de novo
  // 4xx é sempre determinístico. 429 tem retry controlado pelo servidor.
  return error.status >= 400 && error.status < 500;
}
